import { RichTextContent } from '@/components/molecules/rich-text-content';
import { Check } from 'lucide-react';
import { LineItemPhoto } from './LineItemPhoto';
import { isMembershipLine, isMembershipPurchaseLine } from '@notifi/shared-types/membership-line';

import type { EstimateSelections } from '@/lib/quote/useEstimateSelections';
import type { EstimateLineItem, EstimateMembershipOffer } from '@/types/estimate';
import { lineDetailVisibility } from '@/lib/sales/line-detail-visibility';
import { resolveEstimateLineAmounts } from '@/lib/sales/line-item-pricing';
import { membershipOfferAdjustmentForLines } from '@/lib/sales/membership-offer';
import { optionalKey } from '@/lib/sales/optional-line-key';
import { formatQuantityWithUnit } from '@/lib/sales/quantity-display';
import { offerCountedLines } from '@/lib/sales/service-components';

/**
 * The customer-facing OPTIONAL ADD-ON cards, shared by both estimate surfaces
 * (public quote + logged-in portal). Each offer is a checkbox card the customer
 * ticks to add the service to the sale; the running total upstream reprices from
 * the same `useEstimateSelections` state that feeds `offers`/`optionalSelections`
 * here, so both surfaces move money identically.
 *
 * `canAct` is false once the estimate is no longer actionable (terminal status /
 * expired): the cards then render as a read-only record of what was offered.
 * `fmt` is injected so each surface prints currency in its own house format
 * while the layout stays identical.
 */
export function EstimateAddOnsSection({
  offers,
  optionalSelections,
  toggleOptional,
  canAct,
  fmt,
  variant = 'default',
  membershipOffer = null,
  allLines = [],
  useMemberPricing = false,
  showDescription = true,
  showImage = false,
  showQuantity = true,
  showUnitPrice = true,
}: {
  offers: EstimateSelections['displayOptionalOffers'];
  optionalSelections: EstimateSelections['optionalSelections'];
  toggleOptional: EstimateSelections['toggleOptional'];
  canAct: boolean;
  fmt: (amount: number) => string;
  /** `single-scope` matches the artifact: terracotta selection + a compact
   *  `+$NNN` price pill instead of the Price/Tax/Total breakdown. */
  variant?: 'default' | 'single-scope';
  membershipOffer?: EstimateMembershipOffer | null;
  allLines?: readonly EstimateLineItem[];
  useMemberPricing?: boolean;
  /** The design's itemGroups toggles — add-on descriptions and the quantity ×
   *  unit-price basis follow them like every other line's (per-line
   *  displayConfig still applies on top). */
  showDescription?: boolean;
  showImage?: boolean;
  showQuantity?: boolean;
  showUnitPrice?: boolean;
}) {
  // Memberships are rendered by the dedicated offer below the estimate totals
  // because accepting one reprices the covered work. Keeping that line out of
  // this generic add-on list prevents charging the plan without applying its
  // frozen member-price adjustment.
  const indexedOffers = offers.map((offer, index) => ({ offer, index }));
  const serviceOffers = indexedOffers.filter(({ offer }) => !isMembershipLine(offer.parent));
  const selectedMembershipOffers = membershipOffer
    ? []
    : indexedOffers.filter(({ offer, index }) => {
        return (
          isMembershipPurchaseLine(offer.parent) &&
          Boolean(optionalSelections[optionalKey(offer.parent, index)])
        );
      });
  if (serviceOffers.length === 0 && selectedMembershipOffers.length === 0) return null;
  const isSingle = variant === 'single-scope';
  return (
    <div className="mt-8 space-y-5">
      {serviceOffers.length > 0 ? (
        <div>
          {isSingle ? (
            <>
              <h3 className="text-[22px] font-normal tracking-[-0.026em] text-[#17171A]">
                Recommended while we are there
              </h3>
              <p className="mt-2 text-[14.5px] leading-[1.6] text-[#63646B]">
                Optional extras you can add to this visit. Tick any you&apos;d like to include.
              </p>
            </>
          ) : (
            <>
              <h3 className="text-base font-bold text-[#2B373F]">Optional Services</h3>
              <p className="mt-1 text-sm text-gray-500">
                Add-ons are optional. Check any you&apos;d like to include.
              </p>
            </>
          )}
          <div className={isSingle ? 'mt-5 space-y-[13px]' : 'mt-4 space-y-3'}>
            {serviceOffers.map(({ offer, index: offerIndex }) => {
              const li = offer.parent;
              const rowKey = optionalKey(li, offerIndex);
              const selected = !!optionalSelections[rowKey];
              // Priced at exactly what this checkbox moves — the service and
              // the parts that go with it. A declined service takes its parts
              // out of the sale (`offerCountedLines`), so ticking it adds their
              // money too; included parts carry none of their own and add $0.
              const counted = offerCountedLines(offer, true);
              const amounts = counted
                .map((line) => resolveEstimateLineAmounts(line))
                .reduce(
                  (sum, line) => ({
                    lineSubtotal: sum.lineSubtotal + line.lineSubtotal,
                    lineTax: sum.lineTax + line.lineTax,
                    lineTotal: sum.lineTotal + line.lineTotal,
                  }),
                  { lineSubtotal: 0, lineTax: 0, lineTotal: 0 },
                );
              const memberAdjustment = useMemberPricing
                ? membershipOfferAdjustmentForLines(membershipOffer, counted, allLines)
                : null;
              const displayAmounts = memberAdjustment
                ? {
                    lineSubtotal: memberAdjustment.memberSubtotal,
                    lineTax: memberAdjustment.memberTax,
                    lineTotal: memberAdjustment.memberTotal,
                  }
                : amounts;
              // The same line, offered as a checkbox instead of a
              // table row, says the same thing about itself.
              const offerDetail = lineDetailVisibility(li.displayConfig);
              return (
                <button
                  key={`${li.lineItemId}-opt-${offerIndex}`}
                  type="button"
                  disabled={!canAct}
                  className={
                    isSingle
                      ? `w-full cursor-pointer rounded-[10px] border px-[17px] py-[15px] text-left transition-shadow ${
                          selected
                            ? 'border-[var(--ss-accent)] shadow-[0_0_0_1px_var(--ss-accent)]'
                            : 'border-[#14141A]/[0.13] bg-white'
                        } ${!canAct ? 'pointer-events-none opacity-75' : ''}`
                      : `w-full cursor-pointer rounded-lg border-2 p-4 text-left transition-colors ${
                          selected ? 'border-[#93C5FD] bg-[#EFF6FF]' : 'border-gray-200 bg-white'
                        } ${!canAct ? 'pointer-events-none opacity-75' : ''}`
                  }
                  onClick={() => canAct && toggleOptional(rowKey)}
                >
                  <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                    <div
                      className={
                        isSingle
                          ? 'flex min-w-0 flex-1 items-start gap-[13px]'
                          : 'flex min-w-0 flex-1 items-start gap-3'
                      }
                    >
                      {/* A plain visual checkbox (a span with role="checkbox"), NOT the
                      Radix Checkbox — that renders its own <button>, and this card
                      is already a button, so nesting them is invalid HTML. The
                      card owns the click; this only mirrors the checked state. */}
                      <span
                        role="checkbox"
                        aria-checked={selected}
                        className={
                          isSingle
                            ? `mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border ${
                                selected
                                  ? 'border-[var(--ss-accent)] bg-[var(--ss-accent)] text-white'
                                  : 'border-[#95969D] bg-white'
                              }`
                            : `mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border ${
                                selected
                                  ? 'border-[#2563EB] bg-[#2563EB] text-white'
                                  : 'border-gray-300 bg-white'
                              }`
                        }
                      >
                        {selected ? <Check className="size-3" /> : null}
                      </span>
                      {showImage ? <LineItemPhoto line={li} /> : null}
                      <div className="min-w-0 flex-1">
                        <p
                          className={
                            isSingle
                              ? 'break-words text-[14.5px] font-medium tracking-[-0.014em] text-[#17171A] [overflow-wrap:anywhere]'
                              : 'break-words font-semibold text-[#2B373F] [overflow-wrap:anywhere]'
                          }
                        >
                          {li.name}
                        </p>
                        {showDescription && offerDetail.description && li.description && (
                          <RichTextContent
                            as="span"
                            content={li.description}
                            className={
                              isSingle
                                ? 'mt-1 block break-words text-[13px] leading-[1.5] text-[#63646B] [overflow-wrap:anywhere]'
                                : 'mt-0.5 block break-words text-sm text-gray-500 [overflow-wrap:anywhere]'
                            }
                          />
                        )}
                        {offerDetail.included && offer.components.length > 0 && (
                          <ul
                            className={
                              isSingle
                                ? 'mt-1.5 space-y-0.5 border-l border-[#14141A]/[0.08] pl-3'
                                : 'mt-1.5 space-y-0.5 border-l border-gray-200 pl-3'
                            }
                          >
                            {offer.components.map((part, partIndex) => (
                              <li
                                key={`${part.lineKey ?? part.lineItemId}-${partIndex}`}
                                className={
                                  isSingle
                                    ? 'break-words text-xs text-[#95969D] [overflow-wrap:anywhere]'
                                    : 'break-words text-xs text-gray-500 [overflow-wrap:anywhere]'
                                }
                              >
                                {part.name}
                                {showQuantity && part.quantity > 1 ? ` × ${part.quantity}` : ''}
                                <span className="text-gray-400"> included</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {!isSingle && showQuantity ? (
                          <p className="mt-1 text-xs text-gray-400">
                            {formatQuantityWithUnit(li.quantity, li.unit)}
                            {showUnitPrice ? (
                              <>
                                {' '}
                                &times;{' '}
                                {fmt(
                                  (Number(li.quantity) || 0) > 0
                                    ? displayAmounts.lineSubtotal / (Number(li.quantity) || 1)
                                    : Number(li.basePrice) || 0,
                                )}
                              </>
                            ) : null}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    {isSingle ? (
                      // Compact price pill — the artifact's `.addon__p`: a single
                      // "+$NNN" figure, tax folded in, no per-line breakdown.
                      <span className="ml-7 shrink-0 text-[15px] font-medium tabular-nums text-[#17171A] sm:ml-0">
                        +{fmt(displayAmounts.lineTotal)}
                      </span>
                    ) : (
                      <div className="ml-7 grid min-w-0 grid-cols-2 gap-2 border-t border-gray-200 pt-3 text-left sm:ml-0 sm:block sm:flex-shrink-0 sm:border-0 sm:pt-0 sm:text-right">
                        <div className="min-w-0">
                          <p className="break-words text-xs text-gray-500 [overflow-wrap:anywhere]">
                            Price {fmt(displayAmounts.lineSubtotal)}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="break-words font-semibold text-[#2B373F] [overflow-wrap:anywhere] sm:mt-1">
                            Total {fmt(displayAmounts.lineTotal)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {selectedMembershipOffers.map(({ offer, index }) => {
        const amounts = resolveEstimateLineAmounts(offer.parent);
        return (
          <section
            key={`${offer.parent.lineItemId}-membership-${index}`}
            aria-label="Selected membership"
            className="rounded-[10px] border border-[#F6A27C] bg-[#FEF1EB] px-4 py-3.5"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-[#C84E1B]">
              Membership
            </p>
            <div className="mt-1 flex items-center justify-between gap-4">
              <p className="text-sm font-medium text-[#17171A]">{offer.parent.name}</p>
              <p className="shrink-0 text-sm font-medium tabular-nums text-[#17171A]">
                {fmt(amounts.lineTotal)}
              </p>
            </div>
            <p className="mt-0.5 text-xs text-[#63646B]">Included in this estimate.</p>
          </section>
        );
      })}
    </div>
  );
}
